import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Table,
  Statistic,
  Tag,
  Tabs,
  Progress,
  Space,
  Tooltip,
  message,
  Spin,
  Modal,
  Timeline,
  Badge,
  Collapse,
  Typography,
  Alert,
  Avatar,
  Divider,
  Descriptions,
  Empty,
  Radio,
  List,
  Segmented,
  theme,
  Dropdown
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
  PieChartOutlined,
  BarChartOutlined,
  ReloadOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  RiseOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  LineChartOutlined,
  TrophyOutlined,
  FireOutlined,
  ThunderboltOutlined,
  LeftOutlined,
  RightOutlined
} from "@ant-design/icons";
import moment from "moment";
import {
  useGetUsageSummaryQuery,
  useGetRestaurantUsageQuery,
  useGetChecklistTypeUsageQuery,
  useExportUsageReportMutation,
} from "./usage.service";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Text, Title } = Typography;
const { useToken } = theme;

// Helper function for status colors
const getStatusColor = (status) => {
  switch (status) {
    case 'excellent': return '#52c41a';
    case 'good': return '#13c2c2';
    case 'average': return '#faad14';
    case 'poor': return '#ff4d4f';
    default: return '#d9d9d9';
  }
};

// Helper function for performance rating
const getPerformanceRating = (percentage) => {
  if (percentage >= 90) return { label: 'Excellent', color: '#52c41a', icon: <TrophyOutlined /> };
  if (percentage >= 75) return { label: 'Good', color: '#13c2c2', icon: <ThunderboltOutlined /> };
  if (percentage >= 60) return { label: 'Average', color: '#faad14', icon: <FireOutlined /> };
  return { label: 'Needs Improvement', color: '#ff4d4f', icon: <WarningOutlined /> };
};

// Restaurant Card Component
const RestaurantSummaryCard = ({ restaurant, onClick }) => {
  const { token } = useToken();
  const rating = getPerformanceRating(restaurant.summary.completionPercentage);
  
  return (
    <div 
      style={{ 
        background: token.colorBgContainer,
        borderRadius: 16,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        marginBottom: 16,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: `1px solid ${token.colorBorderSecondary}`,
        overflow: 'hidden'
      }}
      onClick={() => onClick(restaurant.restaurant._id)}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ padding: 20 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Avatar 
                size={48} 
                icon={<ShopOutlined />} 
                style={{ backgroundColor: token.colorPrimary, borderRadius: 12 }}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {restaurant.restaurant.name}
                  {restaurant.summary.activeDays > 0 && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {restaurant.summary.activeDays} active days
                    </Tag>
                  )}
                </Title>
                <Space size="large" style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    <EnvironmentOutlined /> Last active: {restaurant.lastActivity ? 
                      moment(restaurant.lastActivity).format("DD MMM YYYY") : 'Never'}
                  </Text>
                </Space>
              </div>
            </Space>
          </Col>
          <Col>
            <Button 
              type="primary" 
              ghost 
              icon={<EyeOutlined />}
              onClick={(e) => { e.stopPropagation(); onClick(restaurant.restaurant._id); }}
            >
              View Details
            </Button>
          </Col>
        </Row>
        
        <Divider style={{ margin: '16px 0' }} />
        
        <Row gutter={24}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Completion Rate</Text>
              <div style={{ marginTop: 8 }}>
                <Progress
                  type="circle"
                  percent={restaurant.summary.completionPercentage}
                  width={80}
                  strokeColor={getStatusColor(rating.label.toLowerCase())}
                  format={(percent) => (
                    <span style={{ fontSize: 16, fontWeight: 'bold', color: rating.color }}>
                      {percent}%
                    </span>
                  )}
                />
                <Tag color={rating.color} style={{ marginTop: 8 }}>
                  {rating.icon} {rating.label}
                </Tag>
              </div>
            </div>
          </Col>
          <Col span={18}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic 
                  title="Total Checklists" 
                  value={Object.values(restaurant.byType).reduce((a,b) => a + b, 0)}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Incidents" 
                  value={restaurant.summary.totalIncidents}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: restaurant.summary.totalIncidents > 0 ? '#ff4d4f' : '#52c41a', fontSize: 20 }}
                />
              </Col>
              <Col span={8}>
                <Statistic 
                  title="Deliveries" 
                  value={restaurant.summary.totalDeliveries}
                  prefix={<TeamOutlined />}
                  valueStyle={{ fontSize: 20 }}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <Space size={[8, 16]} wrap>
                {Object.entries(restaurant.byType).map(([type, count]) => (
                  count > 0 && (
                    <Tag key={type} color="processing" style={{ padding: '4px 12px', borderRadius: 20 }}>
                      <strong style={{ textTransform: 'capitalize' }}>{type}:</strong> {count}
                    </Tag>
                  )
                ))}
              </Space>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

// Fixed Daily Activity Card - API ke structure ke according
const DailyActivityCard = ({ day }) => {
  // API se directly arrays aa rahe hain - check karo konsi keys hain
  const hasOpening = day.opening && Array.isArray(day.opening) && day.opening.length > 0;
  const hasClosing = day.closing && Array.isArray(day.closing) && day.closing.length > 0;
  const hasHothold = day.hothold && Array.isArray(day.hothold) && day.hothold.length > 0;
  const hasWeekly = day.weekly && Array.isArray(day.weekly) && day.weekly.length > 0;
  const hasMonthly = day.monthly && Array.isArray(day.monthly) && day.monthly.length > 0;
  
  const hasActivity = hasOpening || hasClosing || hasHothold || hasWeekly || hasMonthly;
  
  const getTypeIcon = (type) => {
    switch(type) {
      case 'opening': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'closing': return <ClockCircleOutlined style={{ color: '#1890ff' }} />;
      case 'hothold': return <FireOutlined style={{ color: '#faad14' }} />;
      case 'weekly': return <CalendarOutlined style={{ color: '#722ed1' }} />;
      case 'monthly': return <CalendarOutlined style={{ color: '#eb2f96' }} />;
      default: return <FileTextOutlined />;
    }
  };
  
  // Get all checklist types with their counts
  const getAllChecklistItems = () => {
    const items = [];
    if (hasOpening) items.push({ type: 'opening', count: day.opening.length });
    if (hasClosing) items.push({ type: 'closing', count: day.closing.length });
    if (hasHothold) items.push({ type: 'hothold', count: day.hothold.length });
    if (hasWeekly) items.push({ type: 'weekly', count: day.weekly.length });
    if (hasMonthly) items.push({ type: 'monthly', count: day.monthly.length });
    return items;
  };
  
  return (
    <div style={{ 
      border: '1px solid #f0f0f0', 
      borderRadius: 12, 
      padding: 16,
      background: hasActivity ? '#f6ffed' : '#fff1f0',
      borderColor: hasActivity ? '#b7eb8f' : '#ffccc7'
    }}>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div>
          <Text strong style={{ fontSize: 16 }}>
            {moment(day.date).format("DD MMM YYYY")}
          </Text>
          <Tag color={hasActivity ? "green" : "error"} style={{ marginLeft: 8 }}>
            {moment(day.date).format("dddd")}
          </Tag>
          {!hasActivity && (
            <Tag color="warning" style={{ marginLeft: 8 }}>
              <WarningOutlined /> No Completed Activity
            </Tag>
          )}
          {hasActivity && (
            <Tag color="success" style={{ marginLeft: 8 }}>
              <CheckCircleOutlined /> {getAllChecklistItems().reduce((sum, item) => sum + item.count, 0)} Tasks
            </Tag>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {!hasActivity ? (
            <div style={{ 
              background: '#fff1f0', 
              padding: '12px 20px', 
              borderRadius: 8,
              width: '100%',
              textAlign: 'center'
            }}>
              <Text type="secondary">
                <WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                No completed checklists on this day
              </Text>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                (Only tasks with status "completed" are shown)
              </div>
            </div>
          ) : (
            getAllChecklistItems().map((item, idx) => (
              <div key={idx} style={{ 
                background: 'white', 
                padding: '8px 16px', 
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                minWidth: 100,
                textAlign: 'center',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                <Space>
                  {getTypeIcon(item.type)}
                  <span style={{ textTransform: 'capitalize' }}>
                    <strong>{item.type}</strong>
                  </span>
                  <Badge 
                    count={item.count} 
                    style={{ backgroundColor: '#52c41a' }} 
                  />
                </Space>
              </div>
            ))
          )}
        </div>
      </Space>
    </div>
  );
};

// Quick Date Range Component
const QuickDateSelector = ({ value, onChange }) => {
  const quickRanges = [
    { label: 'Today', value: 'today', getRange: () => [moment(), moment()] },
    { label: 'Yesterday', value: 'yesterday', getRange: () => [moment().subtract(1, 'day'), moment().subtract(1, 'day')] },
    { label: 'This Week', value: 'thisWeek', getRange: () => [moment().startOf('week'), moment().endOf('week')] },
    { label: 'Last Week', value: 'lastWeek', getRange: () => [moment().subtract(1, 'week').startOf('week'), moment().subtract(1, 'week').endOf('week')] },
    { label: 'This Month', value: 'thisMonth', getRange: () => [moment().startOf('month'), moment().endOf('month')] },
    { label: 'Last Month', value: 'lastMonth', getRange: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')] },
    { label: 'Last 7 Days', value: 'last7Days', getRange: () => [moment().subtract(7, 'days'), moment()] },
    { label: 'Last 30 Days', value: 'last30Days', getRange: () => [moment().subtract(30, 'days'), moment()] },
  ];

  const handleQuickSelect = (range) => {
    const [start, end] = range.getRange();
    onChange([start, end]);
  };

  return (
    <Space wrap size="small">
      {quickRanges.map(range => (
        <Button
          key={range.value}
          size="small"
          onClick={() => handleQuickSelect(range)}
          type={value === range.value ? "primary" : "default"}
        >
          {range.label}
        </Button>
      ))}
    </Space>
  );
};

// Week Navigator Component
const WeekNavigator = ({ value, onChange }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf('week'));
  
  const weekStart = value[0]?.clone().startOf('week');
  const weekEnd = weekStart?.clone().endOf('week');
  
  const goToPreviousWeek = () => {
    const newWeekStart = currentWeekStart.clone().subtract(1, 'week');
    setCurrentWeekStart(newWeekStart);
    onChange([newWeekStart, newWeekStart.clone().endOf('week')]);
  };
  
  const goToNextWeek = () => {
    const newWeekStart = currentWeekStart.clone().add(1, 'week');
    setCurrentWeekStart(newWeekStart);
    onChange([newWeekStart, newWeekStart.clone().endOf('week')]);
  };
  
  const goToCurrentWeek = () => {
    const now = moment();
    setCurrentWeekStart(now.clone().startOf('week'));
    onChange([now.clone().startOf('week'), now.clone().endOf('week')]);
  };
  
  return (
    <Space>
      <Button icon={<LeftOutlined />} onClick={goToPreviousWeek} size="small" />
      <Button size="small" onClick={goToCurrentWeek}>Current Week</Button>
      <Button icon={<RightOutlined />} onClick={goToNextWeek} size="small" />
      <Text strong>
        {weekStart?.format('DD MMM')} - {weekEnd?.format('DD MMM YYYY')}
      </Text>
    </Space>
  );
};

// Month Navigator Component
const MonthNavigator = ({ value, onChange }) => {
  const [currentMonth, setCurrentMonth] = useState(moment());
  
  const monthStart = value[0]?.clone().startOf('month');
  const monthEnd = monthStart?.clone().endOf('month');
  
  const goToPreviousMonth = () => {
    const newMonth = currentMonth.clone().subtract(1, 'month');
    setCurrentMonth(newMonth);
    onChange([newMonth.clone().startOf('month'), newMonth.clone().endOf('month')]);
  };
  
  const goToNextMonth = () => {
    const newMonth = currentMonth.clone().add(1, 'month');
    setCurrentMonth(newMonth);
    onChange([newMonth.clone().startOf('month'), newMonth.clone().endOf('month')]);
  };
  
  const goToCurrentMonth = () => {
    const now = moment();
    setCurrentMonth(now);
    onChange([now.clone().startOf('month'), now.clone().endOf('month')]);
  };
  
  return (
    <Space>
      <Button icon={<LeftOutlined />} onClick={goToPreviousMonth} size="small" />
      <Button size="small" onClick={goToCurrentMonth}>Current Month</Button>
      <Button icon={<RightOutlined />} onClick={goToNextMonth} size="small" />
      <Text strong>{monthStart?.format('MMMM YYYY')}</Text>
    </Space>
  );
};

const UsageDashboard = () => {
  const { token } = useToken();
  const [dateRange, setDateRange] = useState([
    moment().startOf("month"),
    moment().endOf("month")
  ]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedType, setSelectedType] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [dateMode, setDateMode] = useState("range"); // 'range', 'week', 'month'

  const queryParams = {
    startDate: dateRange[0]?.format("YYYY-MM-DD"),
    endDate: dateRange[1]?.format("YYYY-MM-DD"),
    ...(selectedRestaurant !== "all" && { restaurantId: selectedRestaurant })
  };

  const { 
    data: summaryData, 
    isLoading: summaryLoading, 
    refetch: refetchSummary 
  } = useGetUsageSummaryQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  const { 
    data: restaurantDetail, 
    isLoading: detailLoading,
  } = useGetRestaurantUsageQuery(
    {
      restaurantId: selectedRestaurantId,
      startDate: dateRange[0]?.format("YYYY-MM-DD"),
      endDate: dateRange[1]?.format("YYYY-MM-DD"),
    },
    { skip: !selectedRestaurantId }
  );

  const { 
    data: typeTrends, 
    isLoading: trendsLoading 
  } = useGetChecklistTypeUsageQuery(
    {
      checklistType: selectedType,
      ...queryParams,
    },
    { skip: selectedType === "all" || activeTab !== "trends" }
  );

  const [exportReport, { isLoading: exporting }] = useExportUsageReportMutation();

  const handleExport = async () => {
    try {
      const blob = await exportReport({
        startDate: dateRange[0]?.format("YYYY-MM-DD"),
        endDate: dateRange[1]?.format("YYYY-MM-DD"),
        restaurantId: selectedRestaurant !== "all" ? selectedRestaurant : undefined,
      }).unwrap();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `usage-report-${moment().format("YYYY-MM-DD")}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success("Report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      message.error(error?.data?.message || "Failed to export report");
    }
  };

  const handleViewDetails = (restaurantId) => {
    setSelectedRestaurantId(restaurantId);
    setDetailModalVisible(true);
  };

  const handleCloseModal = () => {
    setDetailModalVisible(false);
    setSelectedRestaurantId(null);
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return "#52c41a";
    if (percentage >= 60) return "#faad14";
    if (percentage >= 40) return "#fa8c16";
    return "#f5222d";
  };

  const summary = summaryData?.data;
  const restaurants = summary?.restaurants || [];
  const overall = summary?.overall;

  const getEnhancedStats = (stats, usage) => {
    const total = usage?.total || 0;
    const days = usage?.daily?.filter(d => d.checklists?.length > 0).length || 0;
    const avgPerDay = days > 0 ? (total / days).toFixed(1) : 0;
    return { total, days, avgPerDay };
  };

  // Render date selector based on mode
  const renderDateSelector = () => {
    switch(dateMode) {
      case 'week':
        return <WeekNavigator value={dateRange} onChange={setDateRange} />;
      case 'month':
        return <MonthNavigator value={dateRange} onChange={setDateRange} />;
      default:
        return (
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates)}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        );
    }
  };

  if (summaryLoading && !summaryData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="Loading dashboard data..." />
      </div>
    );
  }

  return (
    <div style={{ background: token.colorBgLayout, minHeight: '100vh', padding: 24 }}>
      {/* Header Section */}
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size="large">
              <div style={{ 
                background: token.colorPrimary, 
                width: 48, 
                height: 48, 
                borderRadius: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BarChartOutlined style={{ fontSize: 28, color: 'white' }} />
              </div>
              <div>
                <Title level={2} style={{ margin: 0 }}>Checklist Usage Dashboard</Title>
                <Text type="secondary">Monitor and analyze checklist completion across all restaurants</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => refetchSummary()}
                loading={summaryLoading}
              >
                Refresh
              </Button>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={handleExport}
                loading={exporting}
              >
                Export Report
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Filter Section */}
      <Card style={{ marginBottom: 24, borderRadius: 16 }}>
        {/* Quick Date Selector */}
        <div style={{ marginBottom: 16 }}>
          <QuickDateSelector value={dateMode} onChange={(range) => {
            setDateRange(range);
            setDateMode("range");
          }} />
        </div>
        
        {/* Date Mode Selector */}
        <div style={{ marginBottom: 16 }}>
          <Radio.Group value={dateMode} onChange={(e) => setDateMode(e.target.value)} buttonStyle="solid">
            <Radio.Button value="range">
              <CalendarOutlined /> Custom Range
            </Radio.Button>
            <Radio.Button value="week">
              <CalendarOutlined /> Week View
            </Radio.Button>
            <Radio.Button value="month">
              <CalendarOutlined /> Month View
            </Radio.Button>
          </Radio.Group>
        </div>
        
        <Row gutter={24}>
          <Col span={12}>
            <div>
              <Text strong>Date Range</Text>
              <div style={{ marginTop: 8 }}>
                {renderDateSelector()}
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div>
              <Text strong>Restaurant Filter</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={selectedRestaurant}
                onChange={setSelectedRestaurant}
                placeholder="All Restaurants"
              >
                <Option value="all">All Restaurants ({restaurants.length})</Option>
                {restaurants.map((item) => (
                  <Option key={item.restaurant._id} value={item.restaurant._id}>
                    {item.restaurant.name}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col span={4}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <Segmented
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { value: 'table', icon: <BarChartOutlined />, label: 'Table' },
                  { value: 'cards', icon: <TeamOutlined />, label: 'Cards' },
                ]}
              />
            </div>
          </Col>
        </Row>
        
        {/* Selected Date Range Display */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
            <CalendarOutlined /> Selected Period: {dateRange[0]?.format('DD MMM YYYY')} - {dateRange[1]?.format('DD MMM YYYY')}
          </Tag>
        </div>
      </Card>

      {/* Tabs */}
      <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: '20px 24px' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane
            tab={<span><BarChartOutlined /> Overview</span>}
            key="overview"
          >
            {/* Overall Summary Stats */}
            {overall && (
              <Row gutter={16} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                    <Statistic
                      title="Total Restaurants"
                      value={overall.totalRestaurants}
                      prefix={<ShopOutlined style={{ fontSize: 20 }} />}
                      valueStyle={{ fontSize: 28 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                    <Statistic
                      title="Checklists Completed"
                      value={overall.totalChecklistsCompleted}
                      prefix={<CheckCircleOutlined style={{ fontSize: 20 }} />}
                      valueStyle={{ color: "#3f8600", fontSize: 28 }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                    <Statistic
                      title="Total Incidents"
                      value={overall.totalIncidents}
                      prefix={<WarningOutlined style={{ fontSize: 20 }} />}
                      valueStyle={{
                        color: overall.totalIncidents > 0 ? "#cf1322" : "#3f8600",
                        fontSize: 28
                      }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                    <Statistic
                      title="Avg Completion Rate"
                      value={overall.averageCompletionPercentage}
                      suffix="%"
                      prefix={<PieChartOutlined style={{ fontSize: 20 }} />}
                      valueStyle={{
                        color: getCompletionColor(overall.averageCompletionPercentage),
                        fontSize: 28
                      }}
                    />
                    <Progress
                      percent={overall.averageCompletionPercentage}
                      showInfo={false}
                      strokeColor={getCompletionColor(overall.averageCompletionPercentage)}
                      style={{ marginTop: 8 }}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* Restaurants View */}
            {viewMode === "table" ? (
              <Table
                columns={[
                  {
                    title: "Restaurant",
                    dataIndex: ["restaurant", "name"],
                    key: "name",
                    fixed: "left",
                    width: 220,
                    render: (name, record) => (
                      <Space>
                        <Avatar icon={<ShopOutlined />} style={{ backgroundColor: token.colorPrimary }} />
                        <div>
                          <strong>{name}</strong>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Last active: {record.lastActivity ? moment(record.lastActivity).format("DD MMM") : 'Never'}
                          </Text>
                        </div>
                      </Space>
                    ),
                  },
                  {
                    title: "Performance",
                    key: "completion",
                    width: 180,
                    render: (_, record) => {
                      const rating = getPerformanceRating(record.summary.completionPercentage);
                      return (
                        <div>
                          <Progress
                            percent={record.summary.completionPercentage}
                            size="small"
                            strokeColor={rating.color}
                            showInfo={false}
                          />
                          <div style={{ marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong style={{ color: rating.color }}>
                              {record.summary.completionPercentage}%
                            </Text>
                            <Tag color={rating.color} style={{ margin: 0 }}>
                              {rating.icon} {rating.label}
                            </Tag>
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: "Checklists by Type",
                    key: "types",
                    width: 250,
                    render: (_, record) => (
                      <Space size={[0, 8]} wrap>
                        {Object.entries(record.byType).map(([type, count]) => (
                          count > 0 && (
                            <Tag key={type} style={{ textTransform: 'capitalize', borderRadius: 12 }}>
                              {type}: {count}
                            </Tag>
                          )
                        ))}
                      </Space>
                    ),
                  },
                  {
                    title: "Incidents",
                    dataIndex: ["summary", "totalIncidents"],
                    key: "incidents",
                    width: 80,
                    align: "center",
                    render: (val) => (
                      <Badge
                        count={val}
                        showZero
                        style={{ backgroundColor: val > 0 ? "#ff4d4f" : "#d9d9d9" }}
                      />
                    ),
                  },
                  {
                    title: "Deliveries",
                    dataIndex: ["summary", "totalDeliveries"],
                    key: "deliveries",
                    width: 80,
                    align: "center",
                    render: (val) => (
                      <Badge
                        count={val}
                        showZero
                        style={{ backgroundColor: val > 0 ? "#13c2c2" : "#d9d9d9" }}
                      />
                    ),
                  },
                  {
                    title: "Active Days",
                    dataIndex: ["summary", "activeDays"],
                    key: "activeDays",
                    width: 100,
                    align: "center",
                    render: (val) => (
                      <Tag color="blue">{val} days</Tag>
                    ),
                  },
                  {
                    title: "Action",
                    key: "action",
                    width: 100,
                    fixed: "right",
                    render: (_, record) => (
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleViewDetails(record.restaurant._id)}
                      >
                        Details
                      </Button>
                    ),
                  },
                ]}
                dataSource={restaurants}
                rowKey={(record) => record.restaurant._id}
                loading={summaryLoading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} restaurants`,
                }}
                scroll={{ x: 1200 }}
              />
            ) : (
              <div>
                {restaurants.map(restaurant => (
                  <RestaurantSummaryCard 
                    key={restaurant.restaurant._id} 
                    restaurant={restaurant} 
                    onClick={handleViewDetails}
                  />
                ))}
              </div>
            )}
          </TabPane>

          <TabPane
            tab={<span><LineChartOutlined /> Trends by Type</span>}
            key="trends"
          >
            <div style={{ marginBottom: 24 }}>
              <Space size="large">
                <Text strong>Checklist Type:</Text>
                <Radio.Group 
                  value={selectedType} 
                  onChange={(e) => setSelectedType(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="all">All Types</Radio.Button>
                  <Radio.Button value="opening">Opening</Radio.Button>
                  <Radio.Button value="closing">Closing</Radio.Button>
                  <Radio.Button value="hothold">Hot Hold</Radio.Button>
                  <Radio.Button value="weekly">Weekly</Radio.Button>
                  <Radio.Button value="monthly">Monthly</Radio.Button>
                </Radio.Group>
              </Space>
            </div>

            {selectedType === "all" ? (
              <Alert
                message="Select a Checklist Type"
                description="Please select a checklist type from the options above to view detailed trends and analytics."
                type="info"
                showIcon
              />
            ) : trendsLoading ? (
              <div style={{ textAlign: "center", padding: 50 }}>
                <Spin size="large" />
              </div>
            ) : !typeTrends?.data ? (
              <Alert
                message="No Data Available"
                description="No trend data found for the selected period and checklist type."
                type="warning"
                showIcon
              />
            ) : (
              <div>
                <Row gutter={16}>
                  <Col span={8}>
                    <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic
                        title="Total Completed"
                        value={typeTrends.data.totalCount || 0}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic
                        title="Active Days"
                        value={typeTrends.data.uniqueDays || 0}
                        prefix={<CalendarOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                      <Statistic
                        title="Daily Average"
                        value={Math.round((typeTrends.data.totalCount || 0) / (typeTrends.data.uniqueDays || 1))}
                        prefix={<RiseOutlined />}
                      />
                    </Card>
                  </Col>
                </Row>

                <div style={{ marginTop: 32 }}>
                  <Title level={5}>Daily Trend Analysis</Title>
                  <div style={{ background: token.colorBgLayout, padding: 20, borderRadius: 12 }}>
                    {typeTrends.data.trends?.map((trend, idx) => {
                      const maxCount = Math.max(...(typeTrends.data.trends?.map(t => t.count) || [0]));
                      const percentage = maxCount > 0 ? (trend.count / maxCount) * 100 : 0;
                      return (
                        <div key={idx} style={{ marginBottom: 16 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                            <Space>
                              <CalendarOutlined />
                              <Text strong>{moment(trend.date).format("DD MMM YYYY")}</Text>
                              <Tag>{moment(trend.date).format("dddd")}</Tag>
                            </Space>
                            <Text strong>{trend.count} checklists completed</Text>
                          </div>
                          <Progress
                            percent={percentage}
                            showInfo={false}
                            strokeColor={token.colorPrimary}
                            trailColor={token.colorBorderSecondary}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {typeTrends.data.byRestaurant?.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <Title level={5}>Restaurant Breakdown</Title>
                    <Collapse>
                      {typeTrends.data.byRestaurant.map((item, idx) => (
                        <Panel
                          header={
                            <Space>
                              <ShopOutlined />
                              <strong>{item._id.restaurant}</strong>
                              <Tag color="processing">{item.count} checklists</Tag>
                            </Space>
                          }
                          key={idx}
                        >
                          <Timeline>
                            {item.tasks?.slice(0, 10).map((task, tidx) => (
                              <Timeline.Item key={tidx}>
                                <div>
                                  <strong>Completed:</strong> {moment(task.completedAt).format("YYYY-MM-DD HH:mm")}
                                  <br />
                                  <Text type="secondary">Task #{task.taskNumber}</Text>
                                </div>
                              </Timeline.Item>
                            ))}
                            {item.tasks?.length > 10 && (
                              <Timeline.Item>
                                ... and {item.tasks.length - 10} more completed tasks
                              </Timeline.Item>
                            )}
                          </Timeline>
                        </Panel>
                      ))}
                    </Collapse>
                  </div>
                )}
              </div>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Restaurant Detail Modal */}
{/* Restaurant Detail Modal */}
<Modal
  title={
    <Space size="large">
      <Avatar 
        size={48} 
        icon={<ShopOutlined />} 
        style={{ backgroundColor: token.colorPrimary, borderRadius: 12 }}
      />
      <div>
        <Title level={4} style={{ margin: 0 }}>
          {restaurantDetail?.data?.restaurant?.name}
        </Title>
        <Text type="secondary">Detailed Usage Analytics</Text>
      </div>
    </Space>
  }
  open={detailModalVisible}
  onCancel={handleCloseModal}
  footer={null}
  width={900}
  style={{ top: 20 }}
>
  <Spin spinning={detailLoading}>
    {restaurantDetail?.data && (
      <>
        {/* Stats Cards */}
        {(() => {
          const usage = restaurantDetail.data.usage;
          const total = usage?.total || 0;
          const days = usage?.daily?.filter(d => 
            (d.opening?.length > 0) || 
            (d.closing?.length > 0) || 
            (d.hothold?.length > 0)
          ).length || 0;
          const avgPerDay = days > 0 ? (total / days).toFixed(1) : 0;
          
          return (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="Total Checklists"
                    value={total}
                    prefix={<FileTextOutlined />}
                    valueStyle={{ fontSize: 24 }}
                  />
                  <Text type="secondary">across {days} active days</Text>
                </Card>
              </Col>
              <Col span={8}>
                <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="Avg. Daily Activity"
                    value={avgPerDay}
                    prefix={<CalendarOutlined />}
                    suffix="tasks"
                    valueStyle={{ fontSize: 24 }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card style={{ borderRadius: 12, textAlign: 'center' }}>
                  <Statistic
                    title="Total Records"
                    value={total}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ fontSize: 24 }}
                  />
                </Card>
              </Col>
            </Row>
          );
        })()}

        {/* Checklist Type Breakdown */}
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>Checklist Type Breakdown</Title>
          <Row gutter={16}>
            {['opening', 'closing', 'hothold', 'weekly', 'monthly'].map(type => {
              const count = restaurantDetail.data.usage?.statistics?.[type]?.count || 0;
              const avgTime = restaurantDetail.data.usage?.statistics?.[type]?.avgCompletionTimeMinutes;
              const getTypeColor = () => {
                switch(type) {
                  case 'opening': return '#52c41a';
                  case 'closing': return '#1890ff';
                  case 'hothold': return '#faad14';
                  case 'weekly': return '#722ed1';
                  case 'monthly': return '#eb2f96';
                  default: return '#d9d9d9';
                }
              };
              return (
                <Col span={4} key={type}>
                  <div style={{ 
                    textAlign: 'center', 
                    padding: 12, 
                    background: token.colorBgLayout, 
                    borderRadius: 8,
                    border: `1px solid ${token.colorBorderSecondary}`
                  }}>
                    <Text type="secondary" style={{ textTransform: 'capitalize' }}>{type}</Text>
                    <div><strong style={{ fontSize: 24, color: getTypeColor() }}>{count}</strong></div>
                    {avgTime && Math.abs(avgTime) > 0 && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Avg: {Math.abs(avgTime)} min
                      </Text>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Daily Activity Timeline */}
        <Title level={5}>Daily Activity Timeline</Title>
        <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
          <List
            dataSource={restaurantDetail.data.usage?.daily || []}
            renderItem={(day) => <DailyActivityCard day={day} />}
            locale={{ emptyText: <Empty description="No activity recorded for this period" /> }}
          />
        </div>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Text type="secondary">
            Showing {restaurantDetail.data.usage?.daily?.length || 0} days of activity
          </Text>
        </div>
      </>
    )}
  </Spin>
</Modal>
    </div>
  );
};

export default UsageDashboard;